import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Users, DollarSign, AlertCircle, TrendingUp, MessageSquare, Bot, BarChart3, Calendar, RefreshCw, Link2, Globe, Gift, Copy, Share2, X } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [groupStats, setGroupStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [topLinks, setTopLinks] = useState([]);
  const [linkSortBy, setLinkSortBy] = useState('clients'); // 'clients' ou 'access'
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchGroupStats();
    fetchSubscriptionStatus();
    checkAndShowReferralModal();
  }, []);

  useEffect(() => {
    fetchTopLinks();
  }, [linkSortBy]);

  const checkAndShowReferralModal = async () => {
    // Verificar se o usuário escolheu não mostrar mais
    const hideReferral = localStorage.getItem('hideReferralModal');
    if (hideReferral === 'true') {
      return;
    }
    
    // Buscar código de indicação
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/referral`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralCode(response.data.referral_code);
      setShowReferralModal(true);
    } catch (error) {
      console.error('Erro ao carregar código de indicação:', error);
    }
  };

  const getReferralLink = () => {
    if (!referralCode) return '';
    const baseUrl = window.location.origin;
    return `${baseUrl}/register?ref=${referralCode}`;
  };

  const copyReferralLink = () => {
    const link = getReferralLink();
    navigator.clipboard.writeText(link);
    toast.success('Link copiado!');
  };

  const shareReferralLink = () => {
    const link = getReferralLink();
    const text = `🎁 Ganhe 7 dias grátis no MeuGestor! Use meu link: ${link}`;
    
    if (navigator.share) {
      navigator.share({
        title: 'MeuGestor - Indicação',
        text: text,
        url: link
      });
    } else {
      navigator.clipboard.writeText(text);
      toast.success('Texto copiado para compartilhar!');
    }
  };

  const closeReferralModal = () => {
    if (dontShowAgain) {
      localStorage.setItem('hideReferralModal', 'true');
    }
    setShowReferralModal(false);
  };

  const fetchTopLinks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user-links-top?sort_by=${linkSortBy}&limit=4`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTopLinks(response.data);
    } catch (error) {
      console.error('Erro ao carregar links:', error);
    }
  };

  const openLink = async (link) => {
    // Registrar acesso
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/user-links/${link.id}/access`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Erro ao registrar acesso:', error);
    }
    // Abrir link
    window.open(link.url, '_blank', 'noopener,noreferrer');
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroupStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats-by-group`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroupStats(response.data.groups || []);
    } catch (error) {
      console.error('Erro ao carregar estatísticas por grupo');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error('Erro ao carregar status da assinatura');
    }
  };

  const handleRenewSubscription = () => {
    navigate('/payment');
  };

  const statCards = [
    {
      title: 'Total de Clientes',
      value: stats?.total_clients || 0,
      icon: Users,
      color: 'violet',
      testId: 'total-clients-card'
    },
    {
      title: 'Clientes Ativos',
      value: stats?.active_clients || 0,
      icon: TrendingUp,
      color: 'emerald',
      testId: 'active-clients-card'
    },
    {
      title: 'Clientes Atrasados',
      value: stats?.overdue_clients || 0,
      icon: AlertCircle,
      color: 'rose',
      testId: 'overdue-clients-card'
    }
  ];

  const financialCards = [
    {
      title: 'Recebido Este Mês',
      value: `R$ ${(stats?.valor_recebido_mes || 0).toFixed(2)}`,
      subtitle: stats?.total_pagamentos_mes ? `${stats.total_pagamentos_mes} pagamento(s)` : '',
      icon: DollarSign,
      color: 'emerald',
      trend: stats?.variacao_percentual,
      testId: 'valor-recebido-mes-card'
    },
    {
      title: 'Recebido Mês Anterior',
      value: `R$ ${(stats?.valor_recebido_mes_anterior || 0).toFixed(2)}`,
      icon: TrendingUp,
      color: 'sky',
      testId: 'valor-mes-anterior-card'
    },
    {
      title: 'Valor a Receber',
      value: `R$ ${(stats?.valor_a_receber || 0).toFixed(2)}`,
      icon: DollarSign,
      color: 'violet',
      testId: 'valor-receber-card'
    },
    {
      title: 'Valores Vencidos',
      value: `R$ ${(stats?.valor_vencido || 0).toFixed(2)}`,
      icon: AlertCircle,
      color: 'rose',
      testId: 'valor-vencido-card'
    }
  ];

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-zinc-950" data-testid="dashboard-page">
      <Sidebar />
      
      {/* Modal de Programa de Indicações */}
      <Dialog open={showReferralModal} onOpenChange={setShowReferralModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-xl">
              <div className="p-2 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl">
                <Gift className="w-6 h-6 text-white" />
              </div>
              Programa de Indicações
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Benefício */}
            <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-emerald-400 mb-1">+30 DIAS</p>
              <p className="text-sm text-emerald-300">de acesso grátis por cada indicação!</p>
            </div>

            {/* Como funciona */}
            <div className="space-y-3">
              <p className="text-slate-300 text-sm">
                Compartilhe seu link e ganhe <strong className="text-emerald-400">30 dias de acesso</strong> para 
                cada amigo que se cadastrar e fizer o primeiro pagamento!
              </p>
              
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 font-bold">1</span>
                <span>Seu amigo se cadastra pelo link</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 font-bold">2</span>
                <span>Ele faz o primeiro pagamento</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/20 text-emerald-400 font-bold">3</span>
                <span>Você recebe +30 dias automaticamente!</span>
              </div>
            </div>

            {/* Link de indicação */}
            <div className="bg-zinc-800 rounded-lg p-3">
              <p className="text-xs text-slate-400 mb-2">Seu link de indicação:</p>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={getReferralLink()}
                  className="flex-1 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white font-mono truncate"
                />
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={copyReferralLink}
                  className="border-zinc-700 hover:bg-zinc-700"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Botão de compartilhar */}
            <Button 
              onClick={shareReferralLink}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Compartilhar Link
            </Button>

            {/* Não mostrar mais */}
            <div className="flex items-center gap-2 pt-2 border-t border-zinc-800">
              <Checkbox
                id="dontShowAgain"
                checked={dontShowAgain}
                onCheckedChange={setDontShowAgain}
                className="border-zinc-600 data-[state=checked]:bg-emerald-600"
              />
              <label 
                htmlFor="dontShowAgain" 
                className="text-xs text-slate-400 cursor-pointer"
              >
                Não mostrar esta mensagem novamente
              </label>
            </div>
          </div>

          <div className="flex justify-between items-center pt-2">
            <button
              onClick={() => navigate('/settings')}
              className="text-xs text-slate-400 hover:text-emerald-400 underline"
            >
              Ver minhas indicações
            </button>
            <Button 
              onClick={closeReferralModal}
              variant="outline"
              className="border-zinc-700 hover:bg-zinc-800"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">Dashboard</h1>
          <p className="text-sm text-gray-600 dark:text-slate-400">Visão geral do seu negócio</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-400">Carregando...</p>
          </div>
        ) : (
          <>
            {/* Atalhos para Links/Painéis */}
            {topLinks.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-bold text-white flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-emerald-500" />
                    Meus Painéis
                  </h2>
                  <div className="flex items-center gap-3">
                    {/* Toggle de visualização */}
                    <div className="flex items-center bg-zinc-800 rounded-lg p-1">
                      <button
                        onClick={() => setLinkSortBy('clients')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          linkSortBy === 'clients'
                            ? 'bg-emerald-600 text-white'
                            : 'text-slate-400 hover:text-white'
                        }`}
                        data-testid="sort-by-clients"
                      >
                        + Clientes
                      </button>
                      <button
                        onClick={() => setLinkSortBy('access')}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                          linkSortBy === 'access'
                            ? 'bg-emerald-600 text-white'
                            : 'text-slate-400 hover:text-white'
                        }`}
                        data-testid="sort-by-access"
                      >
                        + Acessados
                      </button>
                    </div>
                    <button
                      onClick={() => navigate('/meus-links')}
                      className="text-sm text-slate-400 hover:text-emerald-400 transition-colors"
                    >
                      Ver todos
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {topLinks.map((link) => (
                    <button
                      key={link.id}
                      onClick={() => openLink(link)}
                      className="flex flex-col items-center justify-center p-4 bg-zinc-900 border border-zinc-800 rounded-xl hover:border-emerald-500/50 hover:bg-zinc-800/50 transition-all group"
                      data-testid={`dashboard-link-${link.id}`}
                    >
                      <div className="w-14 h-14 rounded-xl bg-zinc-800 flex items-center justify-center overflow-hidden mb-3 group-hover:ring-2 group-hover:ring-emerald-500/50 transition-all">
                        {link.icon_url ? (
                          <img 
                            src={link.icon_url} 
                            alt={link.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <Globe 
                          className="w-7 h-7 text-emerald-500" 
                          style={{ display: link.icon_url ? 'none' : 'block' }}
                        />
                      </div>
                      <span className="text-white text-sm font-medium text-center truncate w-full">
                        {link.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quick Actions - No topo */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6">
              <h2 className="text-lg font-bold text-white mb-4">Ações Rápidas</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <button
                  onClick={() => navigate('/clients')}
                  className="flex flex-col items-center justify-center p-4 bg-zinc-800 border border-zinc-700 rounded-lg hover:border-violet-500 transition-colors"
                  data-testid="quick-action-clients"
                >
                  <Users className="w-6 h-6 text-violet-500 mb-1.5" />
                  <span className="text-white text-sm font-medium text-center">Clientes</span>
                </button>

                <button
                  onClick={() => navigate('/products')}
                  className="flex flex-col items-center justify-center p-4 bg-zinc-800 border border-zinc-700 rounded-lg hover:border-violet-500 transition-colors"
                  data-testid="quick-action-products"
                >
                  <DollarSign className="w-6 h-6 text-emerald-500 mb-1.5" />
                  <span className="text-white text-sm font-medium text-center">Produtos</span>
                </button>

                <button
                  onClick={() => navigate('/templates')}
                  className="flex flex-col items-center justify-center p-4 bg-zinc-800 border border-zinc-700 rounded-lg hover:border-violet-500 transition-colors"
                  data-testid="quick-action-templates"
                >
                  <MessageSquare className="w-6 h-6 text-sky-500 mb-1.5" />
                  <span className="text-white text-sm font-medium text-center">Templates</span>
                </button>

                <button
                  onClick={() => navigate('/whatsapp')}
                  className="flex flex-col items-center justify-center p-4 bg-zinc-800 border border-zinc-700 rounded-lg hover:border-violet-500 transition-colors"
                  data-testid="quick-action-whatsapp"
                >
                  <MessageSquare className="w-6 h-6 text-emerald-500 mb-1.5" />
                  <span className="text-white text-sm font-medium text-center">WhatsApp</span>
                </button>
              </div>
            </div>

            {/* Subscription Status Card */}
            {subscriptionInfo && (
              <div className={`mb-6 p-3 rounded-lg border ${
                subscriptionInfo.days_remaining > 7
                  ? 'bg-blue-900/20 border-blue-700/30'
                  : subscriptionInfo.days_remaining > 0
                    ? 'bg-amber-900/20 border-amber-700/30'
                    : 'bg-red-900/20 border-red-700/30'
              }`}>
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Calendar className={`w-5 h-5 ${
                      subscriptionInfo.days_remaining > 7
                        ? 'text-blue-400'
                        : subscriptionInfo.days_remaining > 0
                          ? 'text-amber-400'
                          : 'text-red-400'
                    }`} />
                    <div>
                      <h3 className={`font-medium text-sm ${
                        subscriptionInfo.days_remaining > 7
                          ? 'text-blue-400'
                          : subscriptionInfo.days_remaining > 0
                            ? 'text-amber-400'
                            : 'text-red-400'
                      }`}>
                        Status da Assinatura
                      </h3>
                      <p className="text-xs text-slate-400">
                        {subscriptionInfo.days_remaining > 0 
                          ? `${subscriptionInfo.days_remaining} dias restantes`
                          : 'Assinatura expirada - renove para continuar usando'
                        }
                        {subscriptionInfo.trial_end && (
                          <span className="ml-1 text-xs">
                            (expira em {new Date(subscriptionInfo.trial_end).toLocaleDateString('pt-BR')})
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleRenewSubscription}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      subscriptionInfo.days_remaining > 7
                        ? 'bg-blue-600 hover:bg-blue-700 text-white'
                        : subscriptionInfo.days_remaining > 0
                          ? 'bg-amber-600 hover:bg-amber-700 text-white'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                    }`}
                    data-testid="btn-dashboard-renew"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Renovar
                  </button>
                </div>
              </div>
            )}

            {/* Atalho Meus Links */}
            <div 
              onClick={() => navigate('/meus-links')}
              className="mb-6 p-3 rounded-lg border bg-emerald-900/20 border-emerald-700/30 cursor-pointer hover:bg-emerald-900/30 transition-colors"
              data-testid="shortcut-meus-links"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Link2 className="w-6 h-6 text-emerald-400" />
                  <div>
                    <h3 className="font-medium text-emerald-400 text-sm">Meus Links</h3>
                    <p className="text-xs text-slate-400">Acesse seus links de servidores salvos</p>
                  </div>
                </div>
                <div className="text-emerald-400">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Stats Grid - Clientes */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {statCards.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div 
                    key={stat.title} 
                    className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-zinc-700 transition-all"
                    data-testid={stat.testId}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Icon className={`w-8 h-8 text-${stat.color}-500`} />
                      <span className={`text-2xl font-bold text-${stat.color}-500`}>{stat.value}</span>
                    </div>
                    <p className="text-sm text-slate-400">{stat.title}</p>
                  </div>
                );
              })}
            </div>

            {/* Financial Stats Grid */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6">
              <h2 className="text-lg font-bold text-white mb-4">Relatório Financeiro</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {financialCards.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div 
                      key={stat.title} 
                      className="bg-zinc-800 rounded-xl p-4"
                      data-testid={stat.testId}
                    >
                      <Icon className={`w-6 h-6 text-${stat.color}-500 mb-2`} />
                      <p className="text-xs text-slate-400 mb-1">{stat.title}</p>
                      <p className={`text-xl font-bold text-${stat.color}-500`}>{stat.value}</p>
                      {stat.subtitle && (
                        <p className="text-xs text-slate-500 mt-1">{stat.subtitle}</p>
                      )}
                      {stat.trend !== undefined && (
                        <div className={`flex items-center gap-1 mt-1 text-xs ${
                          stat.trend > 0 ? 'text-emerald-500' : stat.trend < 0 ? 'text-rose-500' : 'text-slate-400'
                        }`}>
                          {stat.trend > 0 ? '↑' : stat.trend < 0 ? '↓' : '→'}
                          <span>{Math.abs(stat.trend).toFixed(1)}% vs mês anterior</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Gráfico de Comparação de Receita */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <BarChart3 className="w-5 h-5 text-violet-500" />
                <h2 className="text-lg font-bold text-white">Comparação de Receita</h2>
              </div>
              
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={[
                    {
                      name: 'Mês Anterior',
                      valor: stats?.valor_recebido_mes_anterior || 0,
                      pagamentos: stats?.total_pagamentos_mes_anterior || 0
                    },
                    {
                      name: 'Mês Atual',
                      valor: stats?.valor_recebido_mes || 0,
                      pagamentos: stats?.total_pagamentos_mes || 0
                    }
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis 
                    dataKey="name" 
                    stroke="#94a3b8"
                    style={{ fontSize: '14px' }}
                  />
                  <YAxis 
                    stroke="#94a3b8"
                    style={{ fontSize: '12px' }}
                    tickFormatter={(value) => `R$ ${value.toFixed(0)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#18181b',
                      border: '1px solid #27272a',
                      borderRadius: '8px',
                      color: '#ffffff'
                    }}
                    formatter={(value, name) => {
                      if (name === 'valor') return [`R$ ${Number(value).toFixed(2)}`, 'Receita'];
                      return [value, 'Pagamentos'];
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ fontSize: '14px', color: '#94a3b8' }}
                    formatter={(value) => {
                      if (value === 'valor') return 'Receita (R$)';
                      return 'Nº de Pagamentos';
                    }}
                  />
                  <Bar 
                    dataKey="valor" 
                    fill="#8b5cf6" 
                    radius={[8, 8, 0, 0]}
                    name="valor"
                  />
                  <Bar 
                    dataKey="pagamentos" 
                    fill="#10b981" 
                    radius={[8, 8, 0, 0]}
                    name="pagamentos"
                  />
                </BarChart>
              </ResponsiveContainer>

              {stats && stats.variacao_percentual !== undefined && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 text-sm">Variação em relação ao mês anterior:</span>
                    <span className={`text-lg font-bold flex items-center gap-1 ${
                      stats.variacao_percentual > 0 ? 'text-emerald-500' : 
                      stats.variacao_percentual < 0 ? 'text-rose-500' : 'text-slate-400'
                    }`}>
                      {stats.variacao_percentual > 0 ? '↑' : stats.variacao_percentual < 0 ? '↓' : '→'}
                      {Math.abs(stats.variacao_percentual).toFixed(1)}%
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Previsão Futura de Recebimentos */}
            {stats?.previsao_7_dias && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-8">
                <div className="flex items-center gap-2 mb-6">
                  <TrendingUp className="w-6 h-6 text-emerald-500" />
                  <h2 className="text-xl font-bold text-white">Previsão de Recebimentos</h2>
                </div>

                {/* Cards de Previsão */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-zinc-800 rounded-lg p-4 border-l-4 border-emerald-500">
                    <p className="text-sm text-slate-400 mb-1">Próximos 7 dias</p>
                    <p className="text-2xl font-bold text-emerald-500 mb-1">
                      R$ {stats.previsao_7_dias.valor.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {stats.previsao_7_dias.quantidade} {stats.previsao_7_dias.quantidade === 1 ? 'cliente' : 'clientes'}
                    </p>
                  </div>

                  <div className="bg-zinc-800 rounded-lg p-4 border-l-4 border-sky-500">
                    <p className="text-sm text-slate-400 mb-1">Próximos 30 dias</p>
                    <p className="text-2xl font-bold text-sky-500 mb-1">
                      R$ {stats.previsao_30_dias.valor.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {stats.previsao_30_dias.quantidade} {stats.previsao_30_dias.quantidade === 1 ? 'cliente' : 'clientes'}
                    </p>
                  </div>

                  <div className="bg-zinc-800 rounded-lg p-4 border-l-4 border-violet-500">
                    <p className="text-sm text-slate-400 mb-1">Próximos 60 dias</p>
                    <p className="text-2xl font-bold text-violet-500 mb-1">
                      R$ {stats.previsao_60_dias.valor.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {stats.previsao_60_dias.quantidade} {stats.previsao_60_dias.quantidade === 1 ? 'cliente' : 'clientes'}
                    </p>
                  </div>

                  <div className="bg-zinc-800 rounded-lg p-4 border-l-4 border-orange-500">
                    <p className="text-sm text-slate-400 mb-1">Próximos 90 dias</p>
                    <p className="text-2xl font-bold text-orange-500 mb-1">
                      R$ {stats.previsao_90_dias.valor.toFixed(2)}
                    </p>
                    <p className="text-xs text-slate-500">
                      {stats.previsao_90_dias.quantidade} {stats.previsao_90_dias.quantidade === 1 ? 'cliente' : 'clientes'}
                    </p>
                  </div>
                </div>

                {/* Lista de Próximos Vencimentos (7 dias) */}
                {stats.previsao_7_dias.clientes.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-400 mb-3 uppercase tracking-wide">
                      Próximos Vencimentos (7 dias)
                    </h3>
                    <div className="space-y-2">
                      {stats.previsao_7_dias.clientes.map((client) => (
                        <div 
                          key={client.id} 
                          className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg hover:bg-zinc-700 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-2 h-2 rounded-full ${
                              client.days_remaining === 0 ? 'bg-rose-500' :
                              client.days_remaining <= 2 ? 'bg-orange-500' :
                              'bg-emerald-500'
                            }`}></div>
                            <div>
                              <p className="text-white font-medium text-sm">{client.name}</p>
                              <p className="text-xs text-slate-400">
                                {client.days_remaining === 0 ? 'Vence hoje' : 
                                 client.days_remaining === 1 ? 'Vence amanhã' :
                                 `Vence em ${client.days_remaining} dias`}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-emerald-500 font-bold text-sm">
                              R$ {client.amount.toFixed(2)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {new Date(client.payment_date).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gráfico de Previsão */}
                <div className="mt-6 pt-6 border-t border-zinc-800">
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={[
                        { periodo: '7 dias', valor: stats.previsao_7_dias.valor },
                        { periodo: '30 dias', valor: stats.previsao_30_dias.valor },
                        { periodo: '60 dias', valor: stats.previsao_60_dias.valor },
                        { periodo: '90 dias', valor: stats.previsao_90_dias.valor }
                      ]}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis 
                        dataKey="periodo" 
                        stroke="#94a3b8"
                        style={{ fontSize: '12px' }}
                      />
                      <YAxis 
                        stroke="#94a3b8"
                        style={{ fontSize: '11px' }}
                        tickFormatter={(value) => `R$ ${value.toFixed(0)}`}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#18181b',
                          border: '1px solid #27272a',
                          borderRadius: '8px',
                          color: '#ffffff'
                        }}
                        formatter={(value) => [`R$ ${Number(value).toFixed(2)}`, 'Valor Previsto']}
                      />
                      <Bar 
                        dataKey="valor" 
                        fill="#10b981"
                        radius={[6, 6, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Clientes Prestes a Vencer */}
            {stats?.clientes_prestes_vencer && stats.clientes_prestes_vencer.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 mb-8">
                <h2 className="text-xl font-bold text-white mb-4">Clientes Prestes a Vencer</h2>
                <div className="space-y-3">
                  {stats.clientes_prestes_vencer.map((client) => (
                    <div key={client.id} className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                      <div>
                        <p className="text-white font-medium">{client.name}</p>
                        <p className="text-sm text-slate-400">
                          Vence em {client.days_remaining} {client.days_remaining === 1 ? 'dia' : 'dias'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-emerald-500 font-bold">R$ {client.amount.toFixed(2)}</p>
                        <p className="text-xs text-slate-400">{new Date(client.payment_date).toLocaleDateString('pt-BR')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Stats by Group */}
            {groupStats.length > 0 && (
              <div className="mt-6">
                <h2 className="text-lg font-bold text-white mb-3">Receita por Grupo</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {groupStats.map((group, index) => (
                    <div
                      key={group.group_id || 'no-group'}
                      className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-zinc-700 transition-all"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div
                          className="w-12 h-12 rounded-lg flex items-center justify-center"
                          style={{ 
                            backgroundColor: `${group.group_color}20`,
                            borderColor: group.group_color,
                            borderWidth: '2px'
                          }}
                        >
                          <Users className="w-6 h-6" style={{ color: group.group_color }} />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-white">{group.group_name}</h3>
                          <p className="text-sm text-slate-400">{group.total_clients} clientes</p>
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 text-sm">Receita Total:</span>
                          <span className="text-white font-bold">R$ {group.total_revenue.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 text-sm">Ativos:</span>
                          <span className="text-emerald-400 font-medium">{group.active_clients}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-slate-400 text-sm">Atrasados:</span>
                          <span className="text-red-400 font-medium">{group.overdue_clients}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
